import * as Hangul from 'hangul-js'
import { makeAutoObservable, runInAction } from 'mobx'

import attendanceController from '@/controller/attendance.controller'
import type { Attendance, GradeGroup, LessonClassGroup, StudentGroups, StudentGroupT } from '@/dto'
import { AttendanceByStudentId, Day, GroupInfo, SchoolGrade, Student, StudentColl } from '@/dto'
import {
  getLessonStudentType,
  type LessonStudentType,
} from '@/page/Lesson/_components/lessonStudentType'
import { fetchLessonStudentGroups } from '@/page/Lesson/_service/fetchLessonStudentGroups'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { sessionStorageService } from '@/service/storage.service'
import dateUtils from '@/utils/DateUtils'
import { CheckSet } from '@/utils/models'

import { scenarioService } from './scenario.service'

export type ServiceNameT = (typeof RouteName)['lesson'] | (typeof RouteName)['attendance']

export default class StudentGroupAccordionService {
  private _searchText = ''
  studentGroups?: StudentGroups
  sortType: '학년' | '반' = '학년'
  selectedGroupInfo?: GroupInfo
  enterOnly = false // 등원한 학생만 보기

  isAccordionShow = true
  isFloated = false
  attByStudentId?: AttendanceByStudentId // 전체 학생의 출석 정보

  // 가장 최근에 선택된 학생의 아코디언 그룹 Id
  latestSelectedAccordionGroupId?: StudentGroupT['id']
  selectedStudentInfo?: { id: Student['id']; name: Student['name'] }

  // 열리고 닫혔는지에 대한 key값 관리
  private _accordionSet = new CheckSet<GroupInfo['key']>()
  private _classAccordionSet = new CheckSet<GroupInfo['key']>()
  private _lastStudentType?: LessonStudentType
  private _studentGroupsFetchSeq = 0
  serviceName?: ServiceNameT

  constructor(serviceName: ServiceNameT) {
    makeAutoObservable(this)
    this.serviceName = serviceName
    this.sortType = sessionStorageService.getItem(getSortTypeStorageName(serviceName)) ?? '학년'
    this.enterOnly = sessionStorageService.getItem(getEnterOnlyStorageName(serviceName)) ?? false
  }

  private get _studentGroupList(): Array<GradeGroup | LessonClassGroup> {
    if (!this.studentGroups) {
      return []
    }
    return this.sortType === '학년'
      ? this.studentGroups.gradeGroupArr
      : this.studentGroups.lessonClassGroupArr
  }

  get selectedGroup() {
    if (!this.selectedGroupInfo || !this.studentGroups) return

    return this.studentGroups.getGroupFromGroupInfo(this.selectedGroupInfo)
  }

  // 실제로 렌더되는(=학생이 있는) 그룹 목록. 전체 열기/닫기 상태의 기준이 된다.
  private get _gradeAccordionGroupList() {
    const arr = this.studentGroups?.gradeGroupArr ?? []
    return (this.enterOnly ? this.getEnteredStudentListByGroups(arr) : arr).filter(
      ({ students }) => students.isNotEmpty,
    )
  }

  private get _classAccordionGroupList() {
    const arr = this.studentGroups?.lessonClassGroupArr ?? []
    return (this.enterOnly ? this.getEnteredStudentListByGroups(arr) : arr).filter(
      ({ students }) => students.isNotEmpty,
    )
  }

  // 별도 boolean 으로 들고 있으면 개별 토글과 어긋나므로(예: 하나씩 다 열어도 '전체 열기' 유지),
  // 렌더되는 그룹이 모두 열렸는지에서 파생한다.
  get accordionAllOpened() {
    const list = this._gradeAccordionGroupList
    return list.length > 0 && list.every(({ id }) => this._accordionSet.isChecked(id))
  }

  get classAccordionAllOpened() {
    const list = this._classAccordionGroupList
    return list.length > 0 && list.every(({ id }) => this._classAccordionSet.isChecked(id))
  }

  // -> 각 그룹 컴포넌트 내부에서 하는 방법 생각해봐야함
  get enteredStudentList() {
    if (!this.studentGroupList || !this.attByStudentId) return []
    return this.getEnteredStudentListByGroups(this.studentGroupList)
  }

  // 전체 그룹에서 출석한 학생 그룹만 리턴
  private getEnteredStudentListByGroups(studentGroupList: StudentGroupT[]) {
    if (!this.attByStudentId) return []
    const arr: StudentGroupT[] = []

    for (const studentGroup of studentGroupList) {
      const enteredStudentColl = new StudentColl()

      for (const student of studentGroup.students.values()) {
        // 등원중인 학생 추가
        const dayAtt = this.attByStudentId.getDayAtt({ studentId: student.id, day: Day.today })

        if (dayAtt?.lastAttendance?.status === 'ENTER') enteredStudentColl.set(student)
      }

      // 출석중인 학생이 1명이라도 있으면 그룹 추가
      if (enteredStudentColl.size > 0) arr.push({ ...studentGroup, students: enteredStudentColl })
    }

    return arr
  }

  async fetchAttByStudentId() {
    if (!this.studentGroups?.studentColl?.toArr) return
    const attByStudentId = await attendanceController.fetchAttendances({
      startDate: dateUtils.toDateString(Day.today.date, '-'),
      endDate: dateUtils.toDateString(Day.today.date, '-'),
    })
    runInAction(() => (this.attByStudentId = attByStudentId))
  }

  getStudentLastState(studentId: Student['id']) {
    return this.attByStudentId?.getDayAtt({ day: Day.today, studentId })?.lastAttendance?.status
  }

  setIsAccordionShow(visible: boolean) {
    this.isAccordionShow = visible
  }

  setIsFloated(floated: boolean) {
    this.isFloated = floated
  }

  setSelectedGroupInfo(groupInfo?: GroupInfo) {
    this.selectedGroupInfo = groupInfo
  }

  toggleAllAccordionOpened(sortType: StudentGroupAccordionService['sortType']) {
    if (sortType === '학년') {
      if (this.accordionAllOpened) {
        this._accordionSet.clearAllCheck()
      } else {
        this.studentGroups?.gradeGroupArr?.forEach(({ id }) => this._accordionSet.setCheck(id))
      }
    } else {
      if (this.classAccordionAllOpened) {
        this._classAccordionSet.clearAllCheck()
      } else {
        this.studentGroups?.lessonClassGroupArr?.forEach(({ id }) =>
          this._classAccordionSet.setCheck(id),
        )
      }
    }
  }
  // 최초 진입 -> url의 학생 넣어줄 때, 아코디언 같이 열어주기 위한 함수
  openAccordionByGroup(studentGroup: GroupInfo) {
    switch (studentGroup.data.type) {
      case 'student': {
        const student = this.studentGroups?.studentColl?.get(studentGroup.data.studentId)
        if (student) {
          this._accordionSet.setCheck(student.schoolGrade.koLabel)
        }
        break
      }
      case 'grade': {
        this._accordionSet.setCheck(studentGroup.key)
        runInAction(() => {
          this.latestSelectedAccordionGroupId = studentGroup.key as StudentGroupT['id']
        })
        break
      }
      case 'lesson-class':
      default: {
        this._classAccordionSet.setCheck(studentGroup.key)
        runInAction(() => {
          this.latestSelectedAccordionGroupId = studentGroup.key as StudentGroupT['id']
        })
      }
    }
  }

  set searchText(searchValue: string) {
    this._searchText = searchValue
  }
  get isSearching() {
    return this._searchText !== ''
  }
  // 학생 검색
  get studentGroupList() {
    if (!this._studentGroupList) return []
    if (this._searchText === '') {
      return this._studentGroupList
    }

    const searcher = new Hangul.Searcher(this._searchText)

    const searchResultStudentList: StudentGroupT[] = []

    for (const studentGroup of this._studentGroupList) {
      const matchedStudentColl = new StudentColl()
      for (const student of studentGroup.students.values()) {
        if (searcher.search(student.name) !== -1) {
          matchedStudentColl.set(student)
        }
      }

      if (matchedStudentColl.size > 0) {
        searchResultStudentList.push({ ...studentGroup, students: matchedStudentColl })
      }
    }

    return searchResultStudentList
  }
  // 학생 아코디언 목록 -> 아코디언 그룹명(학년, 반)을 클릭할 때 toggle하는 함수
  toggleAccordionByGroupId(groupType: StudentGroupT['type'], groupId: StudentGroupT['id']) {
    if (groupType === 'grade') {
      this._accordionSet.toggleCheck(groupId)
    } else {
      this._classAccordionSet.toggleCheck(groupId)
    }
  }

  isOpenGroupId(groupType: StudentGroupT['type'], groupId: StudentGroupT['id']) {
    return groupType === 'grade'
      ? this._accordionSet.isChecked(groupId)
      : this._classAccordionSet.isChecked(groupId)
  }

  toggleEnterOnly() {
    this.enterOnly = !this.enterOnly
    sessionStorageService.setItem(getEnterOnlyStorageName(this.serviceName!))(this.enterOnly)
    this.setAccordionCheckedList()
  }

  changeSortType(sortType: '학년' | '반') {
    this.sortType = sortType
    sessionStorageService.setItem(getSortTypeStorageName(this.serviceName!))(sortType)
  }

  handleSelectGroup(studentGroup: StudentGroupT | Student) {
    this.selectGroup(studentGroup)
    scenarioService.scenarioPointerAt = {}
  }

  // 체크의 기준이 되는 allList 생성
  private setAccordionCheckedList() {
    const grades =
      this.studentGroups?.gradeGroupArr?.filter(({ students }) => students.isNotEmpty) || []
    const lessonClasses =
      this.studentGroups?.lessonClassGroupArr?.filter(
        (lessonClass) => lessonClass.students.isNotEmpty,
      ) || []

    runInAction(() => {
      if (this.enterOnly) {
        this._accordionSet.allList =
          this.getEnteredStudentListByGroups(grades)?.map(({ name }) => name) || []
        this._classAccordionSet.allList =
          this.getEnteredStudentListByGroups(lessonClasses)?.map(({ name }) => name) || []
      } else {
        this._accordionSet.allList = grades.map(({ name }) => name)
        this._classAccordionSet.allList = lessonClasses.map(({ name }) => name)
      }
    })
  }

  isFetched = false

  async initStudentGroup() {
    await this.fetchUpdateStudentGroups()
    await this.fetchAttByStudentId()
    this.setAccordionCheckedList()
  }

  async fetchUpdateStudentGroups() {
    const pathname = commonRepo.router?.location.pathname ?? ''

    // 재원생/비재원생은 한 인스턴스를 공유하고 열림 키도 학년 koLabel 로 같은 공간을 쓴다. 모드가 바뀌면
    // 이전 모드에서 펼친 학년이 새 모드에도 열린 채로 새므로, 전환 시 열림 상태를 비운다(전환 후 선택
    // 그룹은 아래 openAccordionByGroup·URL 리스너가 다시 펼친다).
    const studentType = getLessonStudentType(pathname)
    if (this._lastStudentType && this._lastStudentType !== studentType) {
      this._accordionSet.clearAllCheck()
      this._classAccordionSet.clearAllCheck()
    }
    this._lastStudentType = studentType

    const fetchSeq = ++this._studentGroupsFetchSeq
    const studentGroups = await fetchLessonStudentGroups(pathname)

    // 모드 전환으로 중첩 호출되면(재원생/guest 는 서로 다른 API) 이전 모드 응답이 늦게 도착해
    // 최신 명단을 덮어쓸 수 있다 — 최신 요청만 반영한다.
    if (fetchSeq !== this._studentGroupsFetchSeq) return

    // 아코디언 블로킹 방지 -> 학생 그룹이 있을 때 `학생 그룹 아코디언`이 노출되기 때문에
    this.studentGroups = studentGroups
    this.isFetched = true

    // 명단이 선택보다 늦게 도착하면(모드 전환·콜드 진입) URL 리스너가 학생을 못 찾고 early-return 해
    // accordion 이 닫힌 채 끝난다. 데이터가 실린 지금 선택 그룹을 다시 펼친다(setCheck 는 멱등).
    if (this.selectedGroupInfo) {
      this.openAccordionByGroup(this.selectedGroupInfo)
    }
  }

  async refetchStudentAttList(attList: Attendance[]) {
    if (!this.attByStudentId) return
    const newAttByStudentId = new AttendanceByStudentId(
      await attendanceController.fetchSpecificDayAttendances(
        attList.map(({ studentId }) => studentId),
        Day.today,
        true,
      ),
    )

    for (const [studentId, attFromStudent] of newAttByStudentId.get()) {
      this.attByStudentId.set(studentId, attFromStudent)
    }
  }

  async deleteStudentAtt(att: Attendance) {
    this.attByStudentId?.delete(att.studentId)
  }

  async getUrlGroupInfoFromPath(
    pathData:
      | {
          tab: string
          groupType: string
          key: string
        }
      | undefined,
  ) {
    if (!pathData?.tab || !pathData?.groupType || !pathData?.key) return

    const groupInfo = (() => {
      switch (pathData?.groupType) {
        case 'grade': {
          const schoolGrade = SchoolGrade.fromKoLabel(
            pathData.key as (typeof SchoolGrade.KO_LABEL)[number],
          )

          if (schoolGrade) {
            return new GroupInfo({
              type: 'grade',
              schoolGrade,
            })
          }
          break
        }
        case 'lesson-class': {
          const studentLessonClass = this.studentGroups?.studentsByLessonClass?.get(
            Number(pathData.key),
          )

          if (studentLessonClass) {
            return new GroupInfo({
              type: 'lesson-class',
              classId: studentLessonClass.id,
              name: studentLessonClass.name ?? '',
            })
          }
          break
        }
        case 'student': {
          const student = this.studentGroups?.getStudent(pathData.key as Student['id'])
          if (student) {
            return new GroupInfo({
              type: 'student',
              studentId: student.id,
              name: student.name,
            })
          }
          break
        }
      }
    })()

    return groupInfo
  }

  // 학생리스트에서 선택시
  private selectGroup(studentGroup: StudentGroupT | Student) {
    const groupInfo = (() => {
      if (studentGroup instanceof Student) {
        return GroupInfo.fromStudent(studentGroup)
      }
      return GroupInfo.fromStudentGroup(studentGroup)
    })()

    this.setSelectedGroupInfo(groupInfo)
  }
}

const getSortTypeStorageName = (serviceName: ServiceNameT) => {
  if (serviceName === RouteName.lesson) {
    return '수업_아코디언_학생타입'
  }
  return '출결_아코디언_학생타입'
}

const getEnterOnlyStorageName = (serviceName: ServiceNameT) => {
  if (serviceName === RouteName.lesson) {
    return '수업_등원한학생만보기'
  }
  return '출결_등원한학생만보기'
}
